import React, {useContext, useMemo, useState} from "react";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
    Printer, ChefHat, Receipt, LayoutGrid, CalendarDays, MonitorSmartphone, Store, ListTree,
    Pencil, Plus, Save, Trash2, Undo2, X,
} from "lucide-react";
import {api} from "../../../config/api";
import secureStorage from "../../../utils/secureStorage";
import usePermissions from "../../../hooks/usePermissions";
import Button from "../../Common/Button";
import {inputClass, ListLoader, SectionActions, SettingsTable, StatusBadge} from "../SettingsUI";
import {
    BoolRow, ChoiceRow, ColorValue, EmptyNote, InfoList, InfoRow, PendingBadge, PosEditContext, TextRow, Toggle,
    useBinding,
} from "./PosConfigUI";
import {
    applyPendingChanges, cancelPosConfigChanges, canEditDevice, decodeValue, deviceConnection, EDITABLE_KEYS, encodeValue,
    flattenSettings, formatSince,
    getJournalSettings, getKdsConfig, getKitchenRouting, getTicketSettings, isMobile, NO_KITCHEN_PRINT,
    parseObject, parseValue, putPosConfigChanges, toBool, toPrinterIdList,
} from "./posConfig";

const fieldClass = inputClass(false);

const LANGUAGES = {fr: "Français", en: "English", ar: "العربية", es: "Español", ru: "Русский"};
const THEMES = {light: "Clair", dark: "Sombre"};
const WEEKDAYS = [
    ["monday", "Lundi"], ["tuesday", "Mardi"], ["wednesday", "Mercredi"], ["thursday", "Jeudi"],
    ["friday", "Vendredi"], ["saturday", "Samedi"], ["sunday", "Dimanche"],
];
const KITCHEN_STYLES = {small: "Petit", normal: "Normal", bold: "Gras", large: "Grand", xlarge: "Très grand"};

// Keys rendered by a dedicated tab; any other key lands in « Autres réglages ».
const KNOWN_KEYS = new Set([
    // Interface
    "app_language", "app_theme", "default_language", "default_theme", "default_search_mode",
    "camera_scanner_enabled", "virtual_keyboard_mode", "onscreen_keyboard_auto", "category_sort_order",
    "catalogue_show_all", "catalogue_show_brands", "catalogue_show_packs", "catalogue_show_recents",
    "catalogue_two_line_categories", "catalogue_categories_two_rows", "product_grid_columns",
    "product_grid_show_price", "product_card_size", "product_card_full_name", "price_check_mode",
    "quickbar_show_channel", "quickbar_show_client", "quickbar_show_refund", "quickbar_show_table",
    "pos_classic_mode", "pos_classic_buttons", "sidebar_collapsed",
    // Ticket & cuisine
    "ticket_settings", "journal_settings", "print_ticket_choice", "print_order_number_on_tickets",
    "kitchen_printer_config", "kitchen_printer_config_products", "kitchen_printer_config_menus",
    "kitchen_order_number_mode",
    // Imprimantes
    "label_printer_config", "label_template_product", "printer_ip", "printer_port", "active_printer_id",
    // Calendrier
    "calendar_view_mode", "commerce_calendar_hours",
    // Écran client
    "customer_display_settings", "customer_display_enabled",
    // Poste & commandes
    "order_tracking_enabled", "order_pager_enabled", "order_pager_qr_confirmation",
    "order_pager_qr_customer_display", "order_pager_show_items", "employee_prompt_enabled",
    "employee_prompt_services", "employee_prompt_stockables", "delivery_fee_default",
    "is_cashier_register", "pending_sales_local_only", "pending_sales_realtime_enabled",
    "online_booking_notifications_enabled", "online_booking_alert_enabled", "catalogue_auto_sync_enabled",
    "logout_require_password", "local_kds_config",
]);
const isKnownKey = (key) => KNOWN_KEYS.has(key) || key.startsWith("labelTemplate:");

const TABS = [
    {key: "printers", label: "Imprimantes", Icon: Printer, editable: true},
    {key: "kitchen", label: "Impression cuisine", Icon: ChefHat, editable: true},
    {key: "ticket", label: "Ticket & journal", Icon: Receipt, editable: true},
    {key: "interface", label: "Interface", Icon: LayoutGrid, editable: true},
    {key: "calendar", label: "Calendrier", Icon: CalendarDays},
    {key: "display", label: "Écran client", Icon: MonitorSmartphone},
    {key: "register", label: "Poste & commandes", Icon: Store, editable: true},
    {key: "other", label: "Autres", Icon: ListTree},
];

const newPrinterId = () => (window.crypto?.randomUUID
    ? window.crypto.randomUUID()
    : `prn-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`);

const isValidHost = (ip) => /^[A-Za-z0-9.-]{1,253}$/.test(ip || "");
const isValidPort = (port) => /^\d+$/.test(String(port)) && Number(port) >= 1 && Number(port) <= 65535;

// ─── Catalogue names (kitchen routing references ids) ────────────────────────

const getList = async (url) => {
    const account = await secureStorage.getItem("selectedAccount");
    const {data} = await api.get(url, {headers: {"X-account-id": account.id}});
    if (Array.isArray(data)) return data;
    return data?.data || data?.items || [];
};

const useCatalogue = (enabled) => {
    const options = {enabled, staleTime: 5 * 60 * 1000};
    const categories = useQuery({
        queryKey: ["pos-config-categories"],
        queryFn: () => getList("/categories?page=1&itemsPerPage=1000"),
        ...options,
    });
    const products = useQuery({
        queryKey: ["pos-config-products"],
        queryFn: () => getList("/products?page=1&itemsPerPage=1000"),
        ...options,
    });
    const menus = useQuery({
        queryKey: ["pos-config-menus"],
        queryFn: () => getList("/menus"),
        ...options,
    });

    return useMemo(() => {
        const items = new Map();
        (products.data || []).forEach(p => {
            items.set(p.id, p.name);
            (p.variants || []).forEach(v => items.set(v.id, `${p.name} – ${v.name || v.value || ""}`.trim()));
        });
        return {
            categoryList: categories.data || [],
            categories: new Map((categories.data || []).map(c => [c.id, c.name])),
            items,
            menus: new Map((menus.data || []).map(m => [m.id, m.name])),
            isLoading: categories.isLoading || products.isLoading || menus.isLoading,
        };
    }, [categories.data, products.data, menus.data, categories.isLoading, products.isLoading, menus.isLoading]);
};

// ─── Tabs ────────────────────────────────────────────────────────────────────

const PrinterEditor = ({printers, onChange}) => {
    const update = (id, patch) => onChange(printers.map(p => (
        p.id === id ? {...p, ...patch} : patch.active ? {...p, active: false} : p
    )));
    const add = () => onChange([
        ...printers,
        {id: newPrinterId(), name: "", ip: "", port: "9100", active: printers.length === 0},
    ]);

    return (
        <div className="py-3 space-y-3">
            {printers.length === 0 && <EmptyNote>Aucune imprimante.</EmptyNote>}
            {printers.map(p => (
                <div key={p.id} className="grid grid-cols-1 sm:grid-cols-[1fr_10rem_6rem_auto_auto] gap-2 sm:items-center">
                    <input aria-label="Nom de l'imprimante" placeholder="Nom (ex. Cuisine)" className={fieldClass}
                           value={p.name} onChange={(e) => update(p.id, {name: e.target.value})}/>
                    <input aria-label="Adresse IP" placeholder="192.168.1.50"
                           className={`${fieldClass} font-mono ${p.ip && !isValidHost(p.ip) ? "!border-red-500" : ""}`}
                           value={p.ip} onChange={(e) => update(p.id, {ip: e.target.value.trim()})}/>
                    <input aria-label="Port" placeholder="9100"
                           className={`${fieldClass} font-mono ${!isValidPort(p.port) ? "!border-red-500" : ""}`}
                           value={p.port} onChange={(e) => update(p.id, {port: e.target.value.trim()})}/>
                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap cursor-pointer">
                        <input type="radio" name="primary-printer" className="text-primary focus:ring-primary"
                               checked={!!p.active} onChange={() => update(p.id, {active: true})}/>
                        Principale
                    </label>
                    <Button variant="ghost" size="sm" icon={Trash2} title="Supprimer l'imprimante"
                            onClick={() => onChange(printers.filter(o => o.id !== p.id))}/>
                </div>
            ))}
            <Button variant="tinted" size="sm" icon={Plus} onClick={add}>Ajouter une imprimante</Button>
        </div>
    );
};

const PrintersTab = ({config, flat}) => {
    const ctx = useContext(PosEditContext);
    const printers = config.printers || [];
    const label = parseObject(flat.label_printer_config);
    const pendingIds = new Set([
        ...(config.pendingChanges?.printers?.upsert || []).map(p => p.id),
    ]);
    const columns = [
        {key: "name", label: "Nom", render: p => (
            <span className="inline-flex items-center gap-2">{p.name || "Sans nom"}{pendingIds.has(p.id) && <PendingBadge/>}</span>
        )},
        {key: "address", label: "Adresse", className: "font-mono", render: p => `${p.ip}${p.port ? `:${p.port}` : ""}`},
        {key: "active", label: "Rôle", render: p => p.active ? <StatusBadge active on="Imprimante principale"/> : "Secondaire"},
    ];
    const deletedCount = (config.pendingChanges?.printers?.delete || []).length;

    return (
        <div className="max-w-4xl p-4 sm:p-6 space-y-6">
            <InfoList title="Imprimantes de tickets"
                      description={ctx.editing
                          ? "L'adresse doit être joignable depuis le réseau du magasin. Le routage cuisine référence chaque imprimante : en supprimer une renvoie ses articles sur l'imprimante principale."
                          : "Imprimantes réseau (ESC/POS) configurées sur cet appareil."}>
                {ctx.editing ? (
                    <PrinterEditor printers={ctx.printersDraft ?? printers} onChange={ctx.setPrintersDraft}/>
                ) : (
                    <div className="py-3 space-y-2">
                        <SettingsTable columns={columns} rows={printers}
                                       empty={{icon: Printer, title: "Aucune imprimante", text: "Cet appareil n'a pas d'imprimante réseau configurée."}}/>
                        {deletedCount > 0 && (
                            <p className="text-xs text-amber-700 dark:text-amber-400">
                                {deletedCount} imprimante{deletedCount > 1 ? "s" : ""} en attente de suppression.
                            </p>
                        )}
                    </div>
                )}
                {!isMobile(config) && (flat.printer_ip || config.legacyPrinter?.ip) && (
                    <InfoRow label="Imprimante par défaut (ancienne configuration)">
                        <span className="font-mono">
                            {flat.printer_ip || config.legacyPrinter?.ip}:{flat.printer_port || config.legacyPrinter?.port || 9100}
                        </span>
                    </InfoRow>
                )}
            </InfoList>

            <InfoList title="Imprimante d'étiquettes" description={ctx.editing ? "Se règle sur la caisse." : undefined}>
                {label ? (
                    <>
                        <BoolRow label="Impression d'étiquettes" value={toBool(label.enabled)}/>
                        <InfoRow label="Nom">{label.name}</InfoRow>
                        <InfoRow label="Adresse">
                            {label.ip ? <span className="font-mono">{label.ip}:{label.port || 9100}</span> : undefined}
                        </InfoRow>
                        <InfoRow label="Format">{label.format}</InfoRow>
                    </>
                ) : (
                    <EmptyNote>Aucune imprimante d'étiquettes configurée.</EmptyNote>
                )}
            </InfoList>
        </div>
    );
};

const MULTI = "__multi__";

const printerNames = (value, printersById) => {
    const ids = toPrinterIdList(value);
    if (ids.includes(NO_KITCHEN_PRINT)) return <span className="text-red-600 dark:text-red-400">Ne pas imprimer</span>;
    if (ids.length === 0) return <span className="text-gray-400">Imprimante principale</span>;
    return ids.map(id => printersById.get(id) || `Imprimante supprimée (${id.slice(0, 8)})`).join(", ");
};

// One routing target: printer, « principale » (no entry) or « ne pas imprimer ».
// A multi-printer value (set on the tablet) is kept as is unless changed.
const RouteSelect = ({label, value, printers, onChange}) => {
    const ids = toPrinterIdList(value);
    const selected = ids.length === 0 ? "" : ids.length > 1 ? MULTI : ids[0];
    return (
        <select aria-label={label} className={`${fieldClass} w-auto min-w-[12rem]`} value={selected}
                onChange={(e) => onChange(e.target.value === "" ? null : e.target.value === MULTI ? value : e.target.value)}>
            <option value="">Imprimante principale</option>
            {printers.map(p => <option key={p.id} value={p.id}>{p.name || p.ip}</option>)}
            <option value={NO_KITCHEN_PRINT}>Ne pas imprimer</option>
            {selected === MULTI && <option value={MULTI}>Plusieurs imprimantes ({ids.length})</option>}
            {selected && selected !== MULTI && selected !== NO_KITCHEN_PRINT && !printers.some(p => p.id === selected) && (
                <option value={selected}>Imprimante supprimée</option>
            )}
        </select>
    );
};

// Routing map editor bound to an EDITABLE_KEYS json key ({id: printerId})
const RoutingEditor = ({title, description, storageKey, rows, printersById, printers, unknownLabel, addOptions}) => {
    const b = useBinding({key: storageKey});
    const map = b?.value || {};
    const [adding, setAdding] = useState("");
    const setRoute = (id, value) => {
        const next = {...map};
        if (value === null) delete next[id]; else next[id] = value;
        b.onChange(next);
    };

    return (
        <InfoList title={title} description={description}>
            {b?.pending && <div className="py-2"><PendingBadge/></div>}
            {rows.length === 0 && !b?.editing && <EmptyNote>Aucun routage spécifique.</EmptyNote>}
            {rows.map(({id, name}) => (
                <InfoRow key={id} label={name || unknownLabel}>
                    {b?.editing
                        ? <RouteSelect label={name || unknownLabel} value={map[id]} printers={printers} onChange={(v) => setRoute(id, v)}/>
                        : printerNames(map[id], printersById)}
                </InfoRow>
            ))}
            {b?.editing && addOptions && (
                <div className="flex items-center gap-2 py-3">
                    <select aria-label="Produit à router" className={`${fieldClass} flex-1`} value={adding}
                            onChange={(e) => setAdding(e.target.value)}>
                        <option value="">Choisir un produit…</option>
                        {addOptions.filter(o => !(o.id in map)).map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                    </select>
                    <Button variant="tinted" size="sm" icon={Plus} disabled={!adding}
                            onClick={() => {
                                setRoute(adding, printers[0]?.id || NO_KITCHEN_PRINT);
                                setAdding("");
                            }}>
                        Ajouter
                    </Button>
                </div>
            )}
        </InfoList>
    );
};

const KitchenTab = ({config, flat}) => {
    const ctx = useContext(PosEditContext);
    const routing = getKitchenRouting(config);
    const ticket = getTicketSettings(config);
    const catalogue = useCatalogue(true);
    const printers = ctx.printersDraft ?? (config.printers || []);
    const printersById = new Map(printers.map(p => [p.id, p.name || p.ip]));
    const styles = ticket?.kitchenTextStyles || {};
    const mobile = isMobile(config);
    const productMap = (ctx.editing && ctx.draft.kitchen_printer_config_products) || routing.products;

    // Edit mode lists every category (to route any of them); read mode only the routed ones
    const categoryRows = ctx.editing
        ? catalogue.categoryList.map(c => ({id: c.id, name: c.name}))
        : Object.keys(routing.categories).map(id => ({id, name: catalogue.categories.get(id)}));
    const productRows = Object.keys(productMap).map(id => ({id, name: catalogue.items.get(id)}));
    const productOptions = [...catalogue.items.entries()].map(([id, name]) => ({id, name}))
        .sort((a, b) => a.name.localeCompare(b.name));

    return (
        <div className="max-w-4xl p-4 sm:p-6 space-y-6">
            <InfoList title="Ticket cuisine">
                {!mobile && <BoolRow label="Routage cuisine" value={routing.enabled}/>}
                {routing.defaultPrinter && (
                    <InfoRow label="Imprimante cuisine par défaut">{printerNames(routing.defaultPrinter, printersById)}</InfoRow>
                )}
                <BoolRow label="Imprimer le ticket cuisine" value={ticket?.printKitchenTicket}
                         bind={{key: "ticket_settings", path: "printKitchenTicket"}}/>
                <BoolRow label="Réimprimer en cuisine à l'encaissement"
                         hint="Pour une vente déjà mise en attente (donc déjà envoyée en cuisine)."
                         value={ticket?.reprintKitchenOnCheckout}
                         bind={{key: "ticket_settings", path: "reprintKitchenOnCheckout"}}/>
                <BoolRow label="Description du produit sur le ticket cuisine" value={ticket?.printKitchenProductDescription}
                         bind={{key: "ticket_settings", path: "printKitchenProductDescription"}}/>
                <ChoiceRow label="Numérotation des commandes" value={parseValue(flat.kitchen_order_number_mode)}
                           options={{local: "Locale (cet appareil)", cloud: "Partagée (cloud)"}}
                           bind={{key: "kitchen_order_number_mode"}}/>
                <BoolRow label="Numéro de commande sur les tickets" value={toBool(flat.print_order_number_on_tickets)}
                         bind={{key: "print_order_number_on_tickets"}}/>
            </InfoList>

            {Object.keys(styles).length > 0 && (
                <InfoList title="Style du ticket cuisine">
                    <ChoiceRow label="Numéro de commande" value={styles.orderNumber} options={KITCHEN_STYLES}/>
                    <ChoiceRow label="Nom du produit" value={styles.productName} options={KITCHEN_STYLES}/>
                    <ChoiceRow label="Options" value={styles.options} options={KITCHEN_STYLES}/>
                    <ChoiceRow label="Note" value={styles.note} options={KITCHEN_STYLES}/>
                </InfoList>
            )}

            {catalogue.isLoading ? (
                <ListLoader label="Chargement du catalogue..."/>
            ) : mobile ? (
                <>
                    <RoutingEditor title="Routage par catégorie"
                                   description="Imprimante cuisine de chaque catégorie. Sans choix, les articles vont sur l'imprimante principale."
                                   storageKey="kitchen_printer_config" rows={categoryRows}
                                   printers={printers} printersById={printersById} unknownLabel="Catégorie supprimée"/>
                    <RoutingEditor title="Routage par produit"
                                   description="Prioritaire sur le routage de la catégorie."
                                   storageKey="kitchen_printer_config_products" rows={productRows}
                                   printers={printers} printersById={printersById} unknownLabel="Produit supprimé"
                                   addOptions={productOptions}/>
                    <InfoList title="Routage des menus"
                              description={ctx.editing
                                  ? "Se règle sur la caisse (Réglages > Impression cuisine)."
                                  : "Utilisé pour les éléments d'un menu, avant le routage produit et catégorie."}>
                        {Object.keys(routing.menus).length === 0 ? (
                            <EmptyNote>Aucun routage spécifique.</EmptyNote>
                        ) : Object.entries(routing.menus).map(([menuId, menu]) => (
                            <div key={menuId} className="py-3 space-y-1">
                                <InfoRow label={<strong>{catalogue.menus.get(menuId) || "Menu supprimé"}</strong>}>
                                    {printerNames(menu?.default, printersById)}
                                </InfoRow>
                                {Object.entries(menu?.items || {}).map(([itemId, value]) => (
                                    <div key={itemId} className="pl-4">
                                        <InfoRow label={catalogue.items.get(itemId) || "Produit supprimé"}>
                                            {printerNames(value, printersById)}
                                        </InfoRow>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </InfoList>
                </>
            ) : (
                <InfoList title="Routage par catégorie"
                          description="Imprimante cuisine de chaque catégorie. Les catégories absentes vont sur l'imprimante par défaut.">
                    {Object.keys(routing.categories).length === 0 ? (
                        <EmptyNote>Aucun routage spécifique.</EmptyNote>
                    ) : Object.entries(routing.categories).map(([id, value]) => (
                        <InfoRow key={id} label={catalogue.categories.get(id) || "Catégorie supprimée"}>
                            {printerNames(value, printersById)}
                        </InfoRow>
                    ))}
                </InfoList>
            )}
        </div>
    );
};

const TicketTab = ({config}) => {
    const t = getTicketSettings(config);
    const journal = getJournalSettings(config);
    const flat = flattenSettings(config);
    const ctx = useContext(PosEditContext);
    const tk = path => ({key: "ticket_settings", path});
    const jn = path => ({key: "journal_settings", path});
    // In edit mode show every option, with the POS defaults when never saved
    const shown = t || (ctx.editing ? decodeValue("ticket_settings", undefined) : null);
    const receiptOff = shown && !shown.printCustomerReceipt;

    return (
        <div className="max-w-4xl p-4 sm:p-6 space-y-6">
            {!shown ? (
                <EmptyNote>Réglages ticket jamais modifiés sur cet appareil : les valeurs par défaut de la caisse s'appliquent.</EmptyNote>
            ) : (
                <>
                    <InfoList title="Configuration d'impression">
                        <BoolRow label="Imprimer le reçu client" value={shown.printCustomerReceipt} bind={tk("printCustomerReceipt")}/>
                        <BoolRow label="Imprimer le ticket cuisine" value={shown.printKitchenTicket} bind={tk("printKitchenTicket")}/>
                        <BoolRow label="Ouvrir le tiroir-caisse" value={shown.openCashDrawer} bind={tk("openCashDrawer")}/>
                        <ChoiceRow label="Ordre d'impression" value={shown.printWorkflow} bind={tk("printWorkflow")} options={{
                            receipt_first: "Reçu client d'abord",
                            kitchen_first: "Ticket cuisine d'abord",
                            simultaneous: "Les deux en même temps",
                        }}/>
                        {flat.print_ticket_choice !== undefined && (
                            <BoolRow label="Demander avant d'imprimer" value={toBool(flat.print_ticket_choice)}/>
                        )}
                    </InfoList>

                    <InfoList title="Options du reçu client"
                              description={receiptOff ? "Le reçu client est désactivé : ces options ne s'appliquent pas." : undefined}>
                        <BoolRow label="Nom du ticket" value={shown.printTicketName} bind={tk("printTicketName")}/>
                        <BoolRow label="Logo" value={shown.printTicketLogo} bind={tk("printTicketLogo")}/>
                        <BoolRow label="Notes des articles" value={shown.printItemNotes ?? shown.printNotes} bind={tk("printItemNotes")}/>
                        <BoolRow label="Note de la vente" value={shown.printSaleNote ?? shown.printNotes} bind={tk("printSaleNote")}/>
                        <BoolRow label="Options des articles" value={shown.printItemOptions} bind={tk("printItemOptions")}/>
                        <TextRow label="Pied de page" value={shown.ticketFooter || shown.footerText} bind={tk("ticketFooter")}/>
                    </InfoList>

                    <InfoList title="Informations affichées">
                        <BoolRow label="ICE" value={shown.showICE} bind={tk("showICE")}/>
                        <BoolRow label="Identifiant fiscal (IF)" value={shown.showIF} bind={tk("showIF")}/>
                        <BoolRow label="Email" value={shown.showEmail} bind={tk("showEmail")}/>
                        <BoolRow label="Téléphone" value={shown.showPhone} bind={tk("showPhone")}/>
                        <BoolRow label="Site web" value={shown.showWebsite} bind={tk("showWebsite")}/>
                        <BoolRow label="Nom de l'employé" value={shown.showEmployeeName} bind={tk("showEmployeeName")}/>
                        <BoolRow label="TVA" value={shown.showTVA} bind={tk("showTVA")}/>
                        <BoolRow label="Détail TVA par article" value={shown.showTVADetail} bind={tk("showTVADetail")}/>
                    </InfoList>

                    <InfoList title="Informations client">
                        <BoolRow label="Imprimer les informations client" value={shown.printCustomerInfo} bind={tk("printCustomerInfo")}/>
                        <BoolRow label="Nom complet" value={shown.showClientName} bind={tk("showClientName")}/>
                        <BoolRow label="Téléphone" value={shown.showClientPhone} bind={tk("showClientPhone")}/>
                        <BoolRow label="Email" value={shown.showClientEmail} bind={tk("showClientEmail")}/>
                        <BoolRow label="Adresse" value={shown.showClientAddress} bind={tk("showClientAddress")}/>
                    </InfoList>

                    <InfoList title="Envoi du ticket">
                        <BoolRow label="Par email" value={shown.sendByEmail} bind={tk("sendByEmail")}/>
                        <BoolRow label="Par WhatsApp" value={shown.sendByWhatsUp} bind={tk("sendByWhatsUp")}/>
                    </InfoList>
                </>
            )}

            <InfoList title="Clôture de caisse (journal)">
                {journal || ctx.editing ? (
                    <>
                        <BoolRow label="Envoyer le journal par email" value={toBool(journal?.sendJournalByEmail)} bind={jn("sendJournalByEmail")}/>
                        <TextRow label="Email par défaut" type="email" value={journal?.defaultEmail} bind={jn("defaultEmail")}/>
                        <BoolRow label="Impression par catégories" value={toBool(journal?.printByCategories)} bind={jn("printByCategories")}/>
                        <BoolRow label="Email par catégories" value={toBool(journal?.emailByCategories)} bind={jn("emailByCategories")}/>
                        <BoolRow label="Supprimer les ventes en attente à la clôture" value={toBool(journal?.deletePendingSalesOnClose)}
                                 bind={jn("deletePendingSalesOnClose")}/>
                    </>
                ) : (
                    <EmptyNote>Valeurs par défaut de la caisse.</EmptyNote>
                )}
            </InfoList>
        </div>
    );
};

const InterfaceTab = ({config, flat}) => {
    const ctx = useContext(PosEditContext);
    const v = key => parseValue(flat[key]);
    const b = key => toBool(flat[key]);
    const employees = useQuery({
        queryKey: ["pos-config-employees"],
        queryFn: () => getList("/employees"),
        staleTime: 5 * 60 * 1000,
    });
    const employeeNames = new Map((employees.data || []).map(e => [e.id, e.name]));
    const employeePrefs = Object.entries(config.settings?.employeePrefs || {});
    const onCaisse = ctx.editing ? "Se règle sur la caisse (préférences de chaque employé)." : undefined;

    return (
        <div className="max-w-4xl p-4 sm:p-6 space-y-6">
            <InfoList title="Langue et thème" description={onCaisse}>
                <ChoiceRow label="Langue de l'interface" value={v("app_language") || config.app?.language} options={LANGUAGES}/>
                <ChoiceRow label="Thème" value={v("app_theme") || config.app?.theme} options={THEMES}/>
                <ChoiceRow label="Langue par défaut (employés)" value={v("default_language")} options={LANGUAGES}/>
                <ChoiceRow label="Thème par défaut (employés)" value={v("default_theme")} options={THEMES}/>
            </InfoList>

            <InfoList title="Recherche et saisie"
                      description={ctx.editing ? "Appliqué à la prochaine ouverture de la Caisse sur l'appareil." : undefined}>
                <ChoiceRow label="Mode de recherche" value={v("default_search_mode")} bind={{key: "default_search_mode"}}
                           options={{barcode: "Code-barres", name: "Nom du produit"}}/>
                <BoolRow label="Scanner avec la caméra" value={b("camera_scanner_enabled")} bind={{key: "camera_scanner_enabled"}}/>
                <ChoiceRow label="Clavier virtuel" bind={{key: "virtual_keyboard_mode"}}
                           value={v("virtual_keyboard_mode") ?? (b("onscreen_keyboard_auto") === undefined ? undefined : b("onscreen_keyboard_auto") ? "auto" : "manual")}
                           options={{auto: "Automatique", manual: "Manuel"}}/>
                {!ctx.editing && <BoolRow label="Vérification de prix" value={b("price_check_mode")}/>}
            </InfoList>

            <InfoList title="Catalogue">
                <ChoiceRow label="Tri des catégories" value={v("category_sort_order")} bind={{key: "category_sort_order"}} options={{
                    backoffice: "Ordre du backoffice", name_asc: "Nom (A → Z)", name_desc: "Nom (Z → A)",
                }}/>
                <BoolRow label="Afficher « Tous »" value={b("catalogue_show_all")} bind={{key: "catalogue_show_all"}}/>
                <BoolRow label="Afficher les marques" value={b("catalogue_show_brands")} bind={{key: "catalogue_show_brands"}}/>
                <BoolRow label="Afficher les packs" value={b("catalogue_show_packs")} bind={{key: "catalogue_show_packs"}}/>
                <BoolRow label="Afficher les récents" value={b("catalogue_show_recents")} bind={{key: "catalogue_show_recents"}}/>
                <BoolRow label="Catégories sur deux lignes" bind={{key: "catalogue_two_line_categories"}}
                         value={b("catalogue_two_line_categories") ?? b("catalogue_categories_two_rows")}/>
                <ChoiceRow label="Colonnes de la grille produits" value={v("product_grid_columns")} bind={{key: "product_grid_columns"}}
                           options={{auto: "Automatique", 5: "5", 6: "6", 7: "7"}}/>
                <ChoiceRow label="Taille des cartes produits" value={v("product_card_size")} bind={{key: "product_card_size"}}
                           options={{small: "Petite", medium: "Moyenne", large: "Grande"}}/>
                <BoolRow label="Prix sur les cartes produits" value={b("product_grid_show_price")} bind={{key: "product_grid_show_price"}}/>
                <BoolRow label="Nom complet sur les cartes" value={b("product_card_full_name")} bind={{key: "product_card_full_name"}}/>
                {flat.pos_classic_mode !== undefined && <BoolRow label="Mode caisse classique" value={b("pos_classic_mode")}/>}
            </InfoList>

            <InfoList title="Barre d'actions rapides">
                <BoolRow label="Bouton Client" value={b("quickbar_show_client")} bind={{key: "quickbar_show_client"}}/>
                <BoolRow label="Bouton Table" value={b("quickbar_show_table")} bind={{key: "quickbar_show_table"}}/>
                <BoolRow label="Bouton Remboursement" value={b("quickbar_show_refund")} bind={{key: "quickbar_show_refund"}}/>
                <BoolRow label="Bouton Canal de vente" value={b("quickbar_show_channel")} bind={{key: "quickbar_show_channel"}}/>
            </InfoList>

            <InfoList title="Préférences des employés" description="Choisies par chaque employé sur cet appareil.">
                {employeePrefs.length === 0 ? (
                    <EmptyNote>Aucune préférence personnelle enregistrée.</EmptyNote>
                ) : employeePrefs.map(([empId, prefs]) => (
                    <InfoRow key={empId} label={employeeNames.get(empId) || "Employé supprimé"}>
                        {[
                            prefs.language && LANGUAGES[prefs.language],
                            prefs.theme && THEMES[prefs.theme],
                            prefs.defaultSection && `Accueil : ${prefs.defaultSection}`,
                            toBool(prefs.showSearch) === false && "Recherche masquée",
                        ].filter(Boolean).join(" · ") || "—"}
                    </InfoRow>
                ))}
            </InfoList>
        </div>
    );
};

const CalendarTab = ({config, flat}) => {
    const hours = parseObject(flat.commerce_calendar_hours);
    const view = parseValue(flat.calendar_view_mode) || config.app?.calendarView;

    return (
        <div className="max-w-4xl p-4 sm:p-6 space-y-6">
            <InfoList title="Affichage">
                <ChoiceRow label="Vue du calendrier" value={view} options={{day: "Jour", week: "Semaine", month: "Mois"}}/>
            </InfoList>
            <InfoList title="Horaires d'ouverture (copie de l'appareil)"
                      description="Les horaires se gèrent dans Paramètres > Horaires d'ouverture ; la caisse les récupère à chaque synchronisation.">
                {!hours ? (
                    <EmptyNote>Aucun horaire enregistré sur cet appareil.</EmptyNote>
                ) : WEEKDAYS.map(([key, label]) => {
                    const day = hours[key];
                    return (
                        <InfoRow key={key} label={label}>
                            {!day ? undefined : day.closed
                                ? <span className="text-red-600 dark:text-red-400">Fermé</span>
                                : <span className="tabular-nums">{day.start} – {day.end}</span>}
                        </InfoRow>
                    );
                })}
            </InfoList>
        </div>
    );
};

const DisplayTab = ({flat}) => {
    const d = parseObject(flat.customer_display_settings);
    const legacyEnabled = toBool(flat.customer_display_enabled);

    if (!d) {
        return (
            <div className="max-w-4xl p-4 sm:p-6 space-y-6">
                <InfoList title="Écran client">
                    <BoolRow label="Écran client" value={legacyEnabled}/>
                </InfoList>
            </div>
        );
    }
    const slides = Array.isArray(d.slideshowImages) ? d.slideshowImages.length : 0;

    return (
        <div className="max-w-4xl p-4 sm:p-6 space-y-6">
            <InfoList title="Écran client (2nd écran)">
                <BoolRow label="Écran client" value={toBool(d.enabled ?? legacyEnabled)}/>
                <BoolRow label="Afficher le panier" value={toBool(d.showCart)}/>
                <BoolRow label="Images des produits" value={toBool(d.showProductImages)}/>
                <BoolRow label="Total HT" value={toBool(d.showTotalHT)}/>
                <BoolRow label="Remises" value={toBool(d.showDiscounts)}/>
                <BoolRow label="Type de commande" value={toBool(d.showOrderType)}/>
                <BoolRow label="Paiements" value={toBool(d.showPayments)}/>
                <BoolRow label="Date et heure" value={toBool(d.showDateTime)}/>
            </InfoList>
            <InfoList title="Apparence">
                <BoolRow label="Logo" value={toBool(d.showLogo)}/>
                {toBool(d.showLogo) && (
                    <ChoiceRow label="Source du logo" value={d.logoSource}
                               options={{account: "Logo du commerce", custom: "Logo personnalisé"}}/>
                )}
                <BoolRow label="Message d'accueil" value={toBool(d.showWelcome)}/>
                {toBool(d.showWelcome) && <InfoRow label="Texte d'accueil">{d.welcomeMessage}</InfoRow>}
                <InfoRow label="Couleur du message"><ColorValue color={d.welcomeColor}/></InfoRow>
                <InfoRow label="Couleur du total"><ColorValue color={d.totalColor}/></InfoRow>
                <ChoiceRow label="Fond" value={d.backgroundType} options={{color: "Couleur", image: "Image"}}/>
                {d.backgroundType !== "image" && <InfoRow label="Couleur de fond"><ColorValue color={d.backgroundColor}/></InfoRow>}
                <BoolRow label="QR code" value={toBool(d.showQr)}/>
                {toBool(d.showQr) && <InfoRow label="Contenu du QR code">{d.qrContent}</InfoRow>}
                <BoolRow label="Animation à l'ajout" value={toBool(d.addAnimation)}/>
                <BoolRow label="Son à l'ajout" value={toBool(d.addSound)}/>
            </InfoList>
            <InfoList title="Diaporama">
                <BoolRow label="Diaporama" value={toBool(d.slideshowEnabled)}/>
                <InfoRow label="Images">{slides ? `${slides} image${slides > 1 ? "s" : ""}` : "Aucune"}</InfoRow>
                <ChoiceRow label="Ajustement" value={d.slideshowFit} options={{cover: "Remplir", contain: "Adapter"}}/>
            </InfoList>
        </div>
    );
};

const RegisterTab = ({flat}) => {
    const b = key => toBool(flat[key]);
    const kds = getKdsConfig(flat);
    const fee = parseValue(flat.delivery_fee_default);

    return (
        <div className="max-w-4xl p-4 sm:p-6 space-y-6">
            <InfoList title="Rôle du poste"
                      description="Le nom de la caisse s'affiche sur ses journaux et sert à filtrer journaux et ventes par caisse (Activité). Facultatif : un numéro suffit.">
                <TextRow label="Nom de la caisse" value={flat.register_name ? String(parseValue(flat.register_name)) : undefined}
                         bind={{key: "register_name"}}/>
                <BoolRow label="Caisse principale" value={b("is_cashier_register")}/>
                <BoolRow label="Ventes en attente locales uniquement" value={b("pending_sales_local_only")}/>
                <BoolRow label="Ventes en attente en temps réel" value={b("pending_sales_realtime_enabled")}/>
                <BoolRow label="Synchronisation auto du catalogue" value={b("catalogue_auto_sync_enabled")}/>
                <BoolRow label="Notifications de réservations en ligne" value={b("online_booking_notifications_enabled")}/>
                <BoolRow label="Alerte sonore des réservations" value={b("online_booking_alert_enabled")}/>
                {flat.logout_require_password !== undefined && (
                    <BoolRow label="Mot de passe à la déconnexion" value={b("logout_require_password")}/>
                )}
            </InfoList>

            <InfoList title="Commandes">
                <BoolRow label="Suivi des commandes" value={b("order_tracking_enabled")}/>
                <BoolRow label="Bipeur / numéro de commande" value={b("order_pager_enabled")}/>
                <BoolRow label="QR code sur la confirmation" value={b("order_pager_qr_confirmation")}/>
                <BoolRow label="QR code sur l'écran client" value={b("order_pager_qr_customer_display")}/>
                <BoolRow label="Articles sur la page de suivi" value={b("order_pager_show_items")}/>
                <InfoRow label="Frais de livraison par défaut">
                    {fee === undefined || fee === null || fee === "" ? undefined : `${fee} MAD`}
                </InfoRow>
            </InfoList>

            <InfoList title="Choix de l'employé à la vente">
                <BoolRow label="Demander l'employé" value={b("employee_prompt_enabled")}/>
                <BoolRow label="Pour les services" value={b("employee_prompt_services")}/>
                <BoolRow label="Pour les produits stockables" value={b("employee_prompt_stockables")}/>
            </InfoList>

            <InfoList title="Serveur cuisine (KDS)" description="Le secret de connexion n'est jamais affiché.">
                {kds ? (
                    <>
                        <BoolRow label="KDS local" value={kds.enabled}/>
                        <InfoRow label="Adresse">{kds.baseUrl && <span className="font-mono">{kds.baseUrl}</span>}</InfoRow>
                        <InfoRow label="Secret">{kds.hasSecret ? "••••••••" : undefined}</InfoRow>
                    </>
                ) : (
                    <EmptyNote>Aucun serveur cuisine configuré.</EmptyNote>
                )}
            </InfoList>
        </div>
    );
};

// Redact anything that looks like a credential before showing raw values
const SECRET_KEY = /secret|token|password|apikey|api_key/i;
const redact = (value) => {
    if (Array.isArray(value)) return value.map(redact);
    if (value && typeof value === "object") {
        return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, SECRET_KEY.test(k) ? "••••••••" : redact(v)]));
    }
    if (typeof value === "string" && value.startsWith("data:")) return "[image]";
    return value;
};

export const formatRawValue = (key, raw) => {
    if (SECRET_KEY.test(key)) return "••••••••";
    const value = parseValue(raw);
    if (typeof value === "boolean") return value ? "Oui" : "Non";
    if (value && typeof value === "object") return JSON.stringify(redact(value), null, 2);
    if (typeof value === "string" && value.startsWith("data:")) return "[image]";
    return String(value);
};

const OtherTab = ({flat}) => {
    const rest = Object.entries(flat).filter(([key]) => !isKnownKey(key)).sort(([a], [b]) => a.localeCompare(b));

    return (
        <div className="max-w-4xl p-4 sm:p-6 space-y-6">
            <InfoList title="Autres réglages" description="Réglages de l'appareil sans écran dédié ici, valeurs brutes.">
                {rest.length === 0 ? (
                    <EmptyNote>Aucun autre réglage.</EmptyNote>
                ) : rest.map(([key, raw]) => {
                    const text = formatRawValue(key, raw);
                    return (
                        <div key={key} className="py-3">
                            <dt className="text-sm font-medium text-gray-700 dark:text-gray-300 font-mono">{key}</dt>
                            <dd className="mt-1">
                                <pre className="text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 rounded-lg p-2 whitespace-pre-wrap break-all max-h-60 overflow-auto">{text}</pre>
                            </dd>
                        </div>
                    );
                })}
            </InfoList>
        </div>
    );
};

const TAB_COMPONENTS = {
    printers: PrintersTab,
    kitchen: KitchenTab,
    ticket: TicketTab,
    interface: InterfaceTab,
    calendar: CalendarTab,
    display: DisplayTab,
    register: RegisterTab,
    other: OtherTab,
};

// ─── Save ────────────────────────────────────────────────────────────────────

const samePrinter = (a, b) => a.name === b.name && a.ip === b.ip && String(a.port) === String(b.port) && !!a.active === !!b.active;

// Draft → PUT body, only what differs from what the device will have
export const buildChangesBody = (config, flat, draft, printersDraft) => {
    const settings = {};
    const removeKeys = [];
    Object.entries(draft).forEach(([key, value]) => {
        const next = encodeValue(key, value);
        if (next === encodeValue(key, decodeValue(key, flat[key]))) return;
        // Texte vidé (nom de caisse) : on retire la clé sur la tablette.
        if (EDITABLE_KEYS[key]?.format === "text" && next === "") removeKeys.push(key);
        else settings[key] = next;
    });

    const upsert = [];
    const remove = [];
    if (printersDraft) {
        const current = new Map((config.printers || []).map(p => [p.id, p]));
        printersDraft.forEach(p => {
            const before = current.get(p.id);
            const printer = {id: p.id, name: p.name.trim(), ip: p.ip.trim(), port: String(p.port).trim(), active: !!p.active};
            if (!before || !samePrinter(before, printer)) upsert.push(printer);
        });
        current.forEach((_, id) => {
            if (!printersDraft.some(p => p.id === id)) remove.push(id);
        });
    }

    const body = {settings, baseVersion: config.pendingVersion ?? 0};
    if (removeKeys.length) body.removeKeys = removeKeys;
    if (upsert.length || remove.length) body.printers = {upsert, delete: remove};
    return Object.keys(settings).length || removeKeys.length || upsert.length || remove.length ? body : null;
};

const formatDateTime = (ms) => !ms ? "" : new Date(Number(ms)).toLocaleString("fr-FR", {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
});

const countChanges = (pending) => Object.keys(pending?.settings || {}).length
    + (pending?.removeKeys || []).length
    + (pending?.printers?.upsert || []).length
    + (pending?.printers?.delete || []).length;

const PendingBanner = ({pending, onCancel, cancelling, canEdit}) => {
    const count = countChanges(pending);
    return (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 px-4 sm:px-6 py-3 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 text-sm text-amber-900 dark:text-amber-200">
            <p className="flex-1">
                <strong>{count} modification{count > 1 ? "s" : ""} en attente.</strong>{" "}
                La caisse les appliquera à sa prochaine synchronisation (environ une minute, si elle est allumée et connectée).
                {pending.updatedBy && <> Modifié par {pending.updatedBy}{pending.updatedAt && ` le ${formatDateTime(pending.updatedAt)}`}.</>}
            </p>
            {canEdit && (
                <Button variant="warning" size="sm" icon={Undo2} loading={cancelling} onClick={onCancel}>
                    Annuler les modifications
                </Button>
            )}
        </div>
    );
};

// La caisse n'interroge plus CE compte : connectée à un autre compte (une
// sauvegarde par compte), éteinte ou hors ligne. Ce qu'on modifie ici attendra.
const AwayBanner = ({config}) => {
    const {state, lastSeen} = deviceConnection(config);
    if (state === "online") return null;
    return (
        <div className="px-4 sm:px-6 py-3 bg-gray-50 dark:bg-gray-800/60 border-b border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-300">
            <strong>
                {state === "away"
                    ? `Cette caisse n'a pas contacté ce compte depuis ${formatSince(lastSeen)} (${lastSeen.toLocaleString("fr-FR", {day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit"})}).`
                    : "Cette caisse n'a encore jamais contacté ce compte depuis la mise à jour."}
            </strong>{" "}
            Elle est peut-être éteinte, hors ligne ou connectée à un autre compte : les modifications faites ici
            ne seront appliquées que lorsqu'elle se reconnectera à ce compte.
        </div>
    );
};

const PosDeviceDetail = ({config: backup}) => {
    const queryClient = useQueryClient();
    const {canUpdate} = usePermissions();
    const [tab, setTab] = useState("printers");
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState({});
    const [printersDraft, setPrintersDraft] = useState(null);

    const config = useMemo(() => applyPendingChanges(backup), [backup]);
    const flat = useMemo(() => flattenSettings(config), [config]);
    const backupFlat = useMemo(() => flattenSettings(backup), [backup]);
    const canEdit = canEditDevice(backup) && canUpdate("settings");
    const activeTab = TABS.find(t => t.key === tab);
    const Tab = TAB_COMPONENTS[tab];

    const stopEditing = () => {
        setEditing(false);
        setDraft({});
        setPrintersDraft(null);
    };
    const refresh = () => {
        queryClient.invalidateQueries({queryKey: ["pos-config", backup.id]});
        queryClient.invalidateQueries({queryKey: ["pos-configs"]});
    };

    const save = useMutation({
        mutationFn: (body) => putPosConfigChanges(backup.id, body),
        onSuccess: () => {
            toast.success("Modifications envoyées à la caisse");
            stopEditing();
            refresh();
        },
        onError: (error) => {
            if (error.response?.status === 409) {
                toast.error("Quelqu'un d'autre a modifié cet appareil entre-temps. Rechargez puis recommencez.");
                refresh();
            } else {
                toast.error(error.response?.data?.error || "Échec de l'enregistrement");
            }
        },
    });
    const cancel = useMutation({
        mutationFn: () => cancelPosConfigChanges(backup.id),
        onSuccess: () => {
            toast.success("Modifications annulées");
            refresh();
        },
        onError: () => toast.error("Échec de l'annulation"),
    });

    const handleSave = () => {
        const invalid = (printersDraft || []).find(p => !isValidHost(p.ip) || !isValidPort(p.port));
        if (invalid) {
            toast.error(`Adresse ou port invalide pour l'imprimante « ${invalid.name || invalid.ip || "sans nom"} »`);
            setTab("printers");
            return;
        }
        const body = buildChangesBody(config, flat, draft, printersDraft);
        if (!body) {
            stopEditing();
            return;
        }
        save.mutate(body);
    };

    const editContext = useMemo(() => ({
        editing, draft, setDraft, flat, backupFlat, pending: backup.pendingChanges,
        printersDraft, setPrintersDraft,
    }), [editing, draft, flat, backupFlat, backup.pendingChanges, printersDraft]);

    return (
        <PosEditContext.Provider value={editContext}>
            {canEdit && (
                <SectionActions>
                    {editing ? (
                        <>
                            <Button variant="plain" size="sm" icon={X} onClick={stopEditing} disabled={save.isPending}>Annuler</Button>
                            <Button variant="plain" size="sm" icon={Save} loading={save.isPending} onClick={handleSave}>Enregistrer</Button>
                        </>
                    ) : activeTab.editable && (
                        <Button variant="plain" size="sm" icon={Pencil} onClick={() => setEditing(true)}>Modifier</Button>
                    )}
                </SectionActions>
            )}
            {canEditDevice(backup) && <AwayBanner config={backup}/>}
            {backup.pendingChanges && (
                <PendingBanner pending={backup.pendingChanges} canEdit={canEdit}
                               onCancel={() => cancel.mutate()} cancelling={cancel.isPending}/>
            )}
            {!canEditDevice(backup) && (
                <p className="px-4 sm:px-6 py-2 text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                    La modification à distance n'est pas encore disponible pour la caisse Desktop.
                </p>
            )}
            <div role="tablist" aria-label="Réglages de l'appareil"
                 className="flex gap-1 overflow-x-auto px-4 sm:px-6 h-14 items-center border-b border-gray-200 dark:border-gray-700">
                {TABS.map(({key, label, Icon}) => (
                    <button key={key} type="button" role="tab" aria-selected={tab === key} onClick={() => setTab(key)}
                            className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-xl text-sm whitespace-nowrap transition-colors ${
                                tab === key
                                    ? "bg-primary/10 dark:bg-primary/20 text-primary-dark dark:text-primary font-medium"
                                    : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                            }`}>
                        <Icon className="w-4 h-4"/>
                        {label}
                    </button>
                ))}
            </div>
            {editing && !activeTab.editable && (
                <p className="px-4 sm:px-6 pt-4 text-sm text-gray-500 dark:text-gray-400">
                    Cet onglet ne se modifie que sur la caisse.
                </p>
            )}
            <Tab config={config} flat={flat}/>
        </PosEditContext.Provider>
    );
};

export default PosDeviceDetail;
